import type {AttachedDocument} from '@domain/refractory-product-catalog'
import {DropdownWithButton, DropdownListItem, ButtonComponentProps, Button} from '@hm/fundamental'
import {mdiFileDocumentOutline, mdiPlus, mdiEyeOutline} from '@mdi/js'
import {forwardRef} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useRefraModal} from '../../providers'

import {RefraCatalogAddDocumentsModal} from './RefraCatalogAddDocumentsModal'
import {RefraCatalogViewDocumentsModal} from './RefraCatalogViewDocumentsModal'

interface RefraCatalogDocumentsDropdownProps {
  refractoryId: number
  documents: AttachedDocument[]
}

export const RefraCatalogDocumentsDropdown: React.FC<RefraCatalogDocumentsDropdownProps> = ({
  refractoryId,
  documents
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {openModal} = useRefraModal()

  const customButton = forwardRef<HTMLButtonElement, ButtonComponentProps>(
    ({label, isDropdownOpen, onClick}, ref) => {
      return (
        <Button
          icon={mdiFileDocumentOutline}
          variant="secondary"
          square
          ref={ref}
          toggled={isDropdownOpen}
          onClick={onClick}
          data-cy="refra-catalog-documents-dropdown-button"
        >
          {label}
        </Button>
      )
    }
  )

  return (
    <DropdownWithButton
      variant="secondary"
      dropdownWidth={200}
      component={customButton}
      data-cy="refra-catalog-documents-dropdown"
    >
      <DropdownListItem
        icon={mdiPlus}
        title={t('refraCatalog.table.addDocument')}
        onClick={() => openModal(<RefraCatalogAddDocumentsModal refractoryId={refractoryId} />)}
        {...(documents.length === 0 ? {className: 'border-none'} : {})}
        data-cy="refra-catalog-documents-dropdown-add-document"
      />
      {documents.length > 0 && (
        <DropdownListItem
          icon={mdiEyeOutline}
          title={t('refraCatalog.table.viewDocument')}
          onClick={() =>
            openModal(
              <RefraCatalogViewDocumentsModal refractoryId={refractoryId} documents={documents} />
            )
          }
          {...{className: 'border-none'}}
          data-cy="refra-catalog-documents-dropdown-view-documents"
        />
      )}
    </DropdownWithButton>
  )
}
